import { apiClient } from "../client";
import { ENDPOINTS } from "../endpoints";

export type AdminTransactionType = "all" | "cash" | "token";

export interface AdminTransactionItem {
  id?: string;
  type?: string;
  walletType?: string | null;
  amount?: number;
  currency?: string;
  paymentMethod?: string | null;
  status?: string;
  createdAt?: string;
  organisation?: string;
  organization?: {
    id?: string;
    name?: string;
  } | null;
  user?: {
    id?: string;
    name?: string;
    email?: string;
    role?: string;
  } | null;
  [key: string]: unknown;
}

export interface AdminTransactionsResponse {
  success?: boolean;
  message?: string;
  data?: {
    type?: AdminTransactionType;
    transactions?: AdminTransactionItem[];
    pagination?: {
      page?: number;
      limit?: number;
      total?: number;
      totalPages?: number;
    };
    cash?: {
      transactions?: AdminTransactionItem[];
      pagination?: {
        page?: number;
        limit?: number;
        total?: number;
        totalPages?: number;
      };
    };
    token?: {
      transactions?: AdminTransactionItem[];
      pagination?: {
        page?: number;
        limit?: number;
        total?: number;
        totalPages?: number;
      };
    };
  };
}

export interface NormalizedAdminTransactions {
  transactions: AdminTransactionItem[];
  pagination?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
}

export const adminTransactionsService = {
  getTransactions: async (payload: {
    type: AdminTransactionType;
    page: number;
    limit: number;
  }): Promise<NormalizedAdminTransactions> => {
    const response = await apiClient.post<AdminTransactionsResponse>(
      ENDPOINTS.ADMIN.TRANSACTIONS,
      payload,
      { params: payload }
    );
    const data: AdminTransactionsResponse['data'] = response.data?.data;
    if (!data) return { transactions: [] };

    if (data.type === "all") {
      const cashTx = data.cash?.transactions ?? [];
      const tokenTx = data.token?.transactions ?? [];
      const combined = [...cashTx, ...tokenTx].sort((a: AdminTransactionItem, b: AdminTransactionItem) => {
        const aTime = new Date((a?.createdAt || a?.created_at || a?.date || 0) as string | number).getTime();
        const bTime = new Date((b?.createdAt || b?.created_at || b?.date || 0) as string | number).getTime();
        return bTime - aTime;
      });
      const cashPag = data.cash?.pagination;
      const tokenPag = data.token?.pagination;
      const total =
        (cashPag?.total || 0) + (tokenPag?.total || 0) || undefined;
      const limit = payload.limit || cashPag?.limit || tokenPag?.limit || 10;
      const totalPages =
        total !== undefined && limit ? Math.ceil(total / limit) : undefined;
      const pagination =
        total !== undefined
          ? {
              page: payload.page ?? cashPag?.page ?? tokenPag?.page ?? 1,
              limit,
              total,
              totalPages,
            }
          : cashPag ?? tokenPag;
      return { transactions: combined, pagination };
    }

    const page = data.pagination?.page ?? payload.page;
    const limit = data.pagination?.limit ?? payload.limit;
    const total = data.pagination?.total;
    const totalPages =
      data.pagination?.totalPages ??
      (total !== undefined && limit ? Math.ceil(total / limit) : undefined);
    return {
      transactions: data.transactions ?? [],
      pagination: {
        page,
        limit,
        total,
        totalPages,
      },
    };
  },
};

export default adminTransactionsService;
